import type { RouteLocationNormalized } from 'vue-router';

import { toCamelCase } from '~~/utils';

/**
 * Campaign parameters stored as key-value pairs
 */
export type AdCampaignCookie = Record<string, string>;

/** Supported campaign parameter keys in URLs */
export const campaignParams: string[] = [
	'ad_campaign_id',
	'ad_set_id',
	'ad_id',
	'ad_name',
	'ad_set',
	'ad_campaign',
	'utm_id',
	'utm_term',
	'utm_content',
	'utm_campaign',
	'utm_source',
	'utm_medium'
];

/** Mapping of UTM parameters to their normalized campaign parameter names */
export const paramMapping = {
	utm_id: 'ad_campaign_id',
	utm_source: 'ad_set_id',
	utm_medium: 'ad_id',
	utm_term: 'ad_name',
	utm_content: 'ad_set',
	utm_campaign: 'ad_campaign'
} as const;

const STORAGE_KEY = 'ad_campaign';
const LOCAL_STORAGE_KEY = 'ad_campaign_bridge';

/** Inactivity timeout matching GA/Mixpanel session semantics (35 min) */
export const CAMPAIGN_INACTIVITY_TTL_MS = 35 * 60 * 1000;

interface CampaignBridge {
	data: AdCampaignCookie;
	lastActivity: number;
}

function parseJSON<T>(raw: string | null): T | null {
	if (!raw) return null;
	try {
		return JSON.parse(raw) as T;
	} catch {
		return null;
	}
}

/**
 * Read campaign data from the localStorage bridge.
 * Returns valid (non-expired) data or an empty object.
 * Cleans up expired entries automatically.
 */
function readBridge(): AdCampaignCookie {
	try {
		const bridge = parseJSON<CampaignBridge>(
			localStorage.getItem(LOCAL_STORAGE_KEY)
		);
		if (!bridge?.data) return {};

		const elapsed = Date.now() - bridge.lastActivity;
		if (elapsed > CAMPAIGN_INACTIVITY_TTL_MS) {
			localStorage.removeItem(LOCAL_STORAGE_KEY);
			return {};
		}

		return bridge.data;
	} catch {
		return {};
	}
}

/**
 * Write campaign data to the localStorage bridge with the current timestamp.
 */
function writeBridge(data: AdCampaignCookie): void {
	try {
		const bridge: CampaignBridge = { data, lastActivity: Date.now() };
		localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(bridge));
	} catch {
		/* quota exceeded or private mode — non-critical */
	}
}

/**
 * Check if a route contains any recognized campaign parameters
 * @param route - Vue Router route object to check
 */
export function routeHasCampaignParams(
	route: RouteLocationNormalized
): boolean {
	const { query } = route;
	return campaignParams.some(key => key in query);
}

/**
 * Extract and normalize campaign parameters from a route
 * @param route - Vue Router route object containing query parameters
 */
export function getCampaignParamsFromRoute(
	route: RouteLocationNormalized
): AdCampaignCookie {
	const { query } = route;
	const campaignData: AdCampaignCookie = {};

	for (const key of Object.keys(query)) {
		if (!campaignParams.includes(key) || typeof query[key] !== 'string')
			continue;

		if (!key.startsWith('utm_')) {
			campaignData[toCamelCase(key)] = query[key] as string;
			continue;
		}

		const mappedKey = paramMapping[key as keyof typeof paramMapping];
		campaignData[toCamelCase(mappedKey)] = query[key] as string;
	}

	return campaignData;
}

/**
 * Persist campaign parameters to both sessionStorage and the localStorage bridge.
 */
export function setAdCampaignInStorage(campaignParams: AdCampaignCookie): void {
	if (typeof window === 'undefined') return;

	try {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(campaignParams));
		writeBridge(campaignParams);
	} catch (error) {
		console.error('Failed to save campaign parameters:', error);
	}
}

/**
 * Read campaign parameters, falling back to the localStorage bridge when
 * sessionStorage is empty (e.g. in a new tab opened via target="_blank").
 */
export function getAdCampaignFromStorage(): AdCampaignCookie {
	if (typeof window === 'undefined') return {};

	try {
		const stored = parseJSON<AdCampaignCookie>(
			sessionStorage.getItem(STORAGE_KEY)
		);
		if (stored && Object.keys(stored).length > 0) return stored;

		return readBridge();
	} catch (error) {
		console.error('Failed to load campaign parameters:', error);
		return {};
	}
}

/**
 * Check if there are active campaign parameters in any storage layer.
 */
export function hasCampaignInStorage(): boolean {
	return Object.keys(getAdCampaignFromStorage()).length > 0;
}

/**
 * Copy valid bridge data into sessionStorage for the current tab.
 * Called on plugin init so new tabs inherit campaign data.
 */
export function syncCampaignToSessionStorage(): void {
	if (typeof window === 'undefined') return;

	const existing = parseJSON<AdCampaignCookie>(
		sessionStorage.getItem(STORAGE_KEY)
	);
	if (existing && Object.keys(existing).length > 0) return;

	const bridgeData = readBridge();
	if (Object.keys(bridgeData).length > 0) {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(bridgeData));
	}
}

/**
 * Heartbeat: refresh the bridge timestamp so the inactivity timer resets.
 * Should be called on every page load and client-side navigation.
 */
export function refreshCampaignActivity(): void {
	if (typeof window === 'undefined') return;

	try {
		const bridge = parseJSON<CampaignBridge>(
			localStorage.getItem(LOCAL_STORAGE_KEY)
		);
		if (!bridge?.data || Object.keys(bridge.data).length === 0) return;

		const elapsed = Date.now() - bridge.lastActivity;
		if (elapsed > CAMPAIGN_INACTIVITY_TTL_MS) {
			localStorage.removeItem(LOCAL_STORAGE_KEY);
			return;
		}

		bridge.lastActivity = Date.now();
		localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(bridge));
	} catch {
		/* non-critical */
	}
}

function areCampaignParamsEqual(
	a: AdCampaignCookie,
	b: AdCampaignCookie
): boolean {
	const keysA = Object.keys(a);
	const keysB = Object.keys(b);
	if (keysA.length !== keysB.length) return false;
	return keysA.every(key => a[key] === b[key]);
}

/**
 * Capture campaign parameters from a route and persist them.
 * Replaces stored data when the URL carries different UTMs and refreshes
 * the inactivity timestamp. Skips the write when params are identical.
 */
export function captureCampaignFromRoute(route: RouteLocationNormalized): void {
	if (!routeHasCampaignParams(route)) return;

	const incoming = getCampaignParamsFromRoute(route);
	const existing = getAdCampaignFromStorage();

	if (areCampaignParamsEqual(existing, incoming)) return;

	setAdCampaignInStorage(incoming);
}
